<script lang="ts" setup>
import { inject, ref, onMounted, onBeforeUnmount, watch, nextTick, provide } from 'vue';
import { HotspotEvents } from '@/viewerExtensions/events';
import { generateUniqueKey } from '@/utils';
import ToggleBox from '@/components/ToggleBox.vue';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import HotspotUpdates from '@/components/Hotspots/HotspotUpdates.vue';
import HotspotAnimations from '@/components/Hotspots/HotspotAnimations.vue';
import HotspotPerspectives from '@/components/Hotspots/HotspotPerspectives.vue';
import HotspotTargets from '@/components/Hotspots/HotspotTargets.vue';
import HotspotFlatContent from '@/components/Hotspots/HotspotFlatContent.vue';

import type { Ref } from 'vue';
import type { Viewer, Nullable, Extension } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestHotspot } from '@/types/IManifest';
import type { Hotspots } from '@/viewerExtensions';
import type { IExtensionGizmoMoveHotspotEvent } from '@/types/events/IExtensionGizmoMoveHotspotEvent';

interface IProps {
  identifier: string;
}

interface IEmits {
  (e: 'delete', value: string): unknown;
}

const props = defineProps<IProps>();
const emit = defineEmits<IEmits>();
const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const hotspot = ref<IManifestHotspot>(manifest.value.hotspots[props.identifier]);
const isFlatContent = ref(hotspot.value.flatContentURL !== null);
const skipFlatContentWatcherRun = ref(false);
const activeHotspot = inject('activeHotspot') as Ref<Nullable<string>>;
const hotspotNameRef = ref<Nullable<HTMLInputElement>>(null);
const hotspotsExtension = viewer.value?.extensions.get('Hotspots') as Extension<Hotspots>;

provide('hotspot', hotspot);

const gizmoMoveHotspotHandler = (event: CustomEvent<IExtensionGizmoMoveHotspotEvent>) => {
  const { targetIndex, position } = event.detail;

  manifest.value.hotspots[props.identifier].position[targetIndex] = { x: position.x, y: position.y, z: position.z };
  hotspotsExtension.moveHotspotTarget(props.identifier, targetIndex, position);
};

const deleteHotspot = () => {
  activeHotspot.value = null;

  delete manifest.value.hotspots[props.identifier];
  delete manifest.value.translations[props.identifier];

  hotspotsExtension.destroyHotspot(props.identifier);

  hotspotsExtension.topLevelHotspots = hotspotsExtension.topLevelHotspots.filter((topLevelHotspot) => topLevelHotspot !== props.identifier);
  hotspotsExtension.currentLevelHotspots = hotspotsExtension.currentLevelHotspots.filter((currentLevelHotspot) => currentLevelHotspot !== props.identifier);

  emit('delete', props.identifier);
};

const mountHandler = () => {
  skipFlatContentWatcherRun.value = true;

  viewer.value?.removeEventListener(HotspotEvents.GIZMO_MOVE_HOTSPOT, gizmoMoveHotspotHandler as EventListener);

  hotspot.value = manifest.value.hotspots[props.identifier];
  isFlatContent.value = hotspot.value.flatContentURL !== null;

  viewer.value?.addEventListener(HotspotEvents.GIZMO_MOVE_HOTSPOT, gizmoMoveHotspotHandler as EventListener);

  skipFlatContentWatcherRun.value = false;
};

const hotspotNameRefBlurHandler = (event: FocusEvent) => {
  const inputValue = (event.target as HTMLInputElement).value;
  if (inputValue.length === 0) return;
  hotspot.value.hotspotName = inputValue;
};

const beforeUnmountHandler = () => {
  viewer.value?.removeEventListener(HotspotEvents.GIZMO_MOVE_HOTSPOT, gizmoMoveHotspotHandler as EventListener);

  hotspotNameRef.value?.removeEventListener('blur', hotspotNameRefBlurHandler);
};

const configureManifestFlatContent = () => {
  if (isFlatContent.value === false) {
    hotspot.value.flatContentURL = null;
    manifest.value.hotspots[props.identifier].flatContentURL = null;
  } else {
    hotspot.value.flatContentURL = '';
    hotspot.value.perspective = null;

    manifest.value.hotspots[props.identifier].flatContentURL = '';
    manifest.value.hotspots[props.identifier].perspective = null;
    manifest.value.hotspots[props.identifier].hotspotUpdates = [];
  }
};

const isFlatContentWatcher = (newValue: boolean, oldValue: boolean) => {
  if (newValue === oldValue) return;

  if (skipFlatContentWatcherRun.value === true) {
    skipFlatContentWatcherRun.value = false;
    return;
  }

  const confirmation = confirm('Are you sure you want to change the hotspot mode? This will delete all configured data for this hotspot');

  if (confirmation === true) {
    configureManifestFlatContent();
  } else {
    skipFlatContentWatcherRun.value = true;

    nextTick(() => {
      isFlatContent.value = oldValue;
    });
  }
};

const hotspotNameWatcher = (newValue: string, oldValue: string) => {
  if (newValue === oldValue) return;
  if (newValue.length === 0) return;

  const newHotspotName = generateUniqueKey(Object.keys(manifest.value.hotspots), newValue);

  activeHotspot.value = null;

  manifest.value.hotspots[props.identifier].hotspotName = newHotspotName;
  manifest.value.hotspots[newHotspotName] = { ...manifest.value.hotspots[props.identifier] };

  hotspotsExtension.renameHotspotTarget(props.identifier, newHotspotName);

  const manifestHotspotValues = Object.values(manifest.value.hotspots);

  for (const hotspot of manifestHotspotValues) {
    for (const hotspotInfo of hotspot.hotspotUpdates) {
      if (hotspotInfo[0] === props.identifier) {
        hotspotInfo[0] = newHotspotName;
        return;
      }
    }
  }

  nextTick(() => {
    activeHotspot.value = newHotspotName;

    delete manifest.value.hotspots[props.identifier];
    delete manifest.value.translations[props.identifier];
  });
};

const hotspotNameRefWatcher = (newValue: Nullable<HTMLInputElement>) => {
  if (!newValue) return;

  newValue.addEventListener('blur', hotspotNameRefBlurHandler);
};

const hotspotStartVisibleWatcher = (newValue: boolean) => {
  if (newValue === true) {
    if (hotspotsExtension.isCurrentLevelTopLevel) {
      hotspotsExtension.topLevelHotspots.push(props.identifier);
      hotspotsExtension.currentLevelHotspots.push(props.identifier);
    }
  } else {
    if (hotspotsExtension.isCurrentLevelTopLevel) {
      const currentLevelHotspotsIndex = hotspotsExtension.currentLevelHotspots.indexOf(props.identifier);
      const topLevelHotspotsIndex = hotspotsExtension.topLevelHotspots.indexOf(props.identifier);

      if (currentLevelHotspotsIndex !== -1) {
        hotspotsExtension.currentLevelHotspots.splice(currentLevelHotspotsIndex, 1);
      }

      if (topLevelHotspotsIndex !== -1) {
        hotspotsExtension.topLevelHotspots.splice(topLevelHotspotsIndex, 1);
      }
    }
  }
};

onMounted(mountHandler);
onBeforeUnmount(beforeUnmountHandler);
watch(isFlatContent, isFlatContentWatcher);
watch(() => hotspot.value.hotspotName, hotspotNameWatcher);
watch(() => hotspot.value.startVisible, hotspotStartVisibleWatcher);
watch(hotspotNameRef, hotspotNameRefWatcher);
</script>

<template>
  <CollapseFieldset class="flex flex-col flex-grow">
    <template #legend>
      Edit Hotspot {{ props.identifier }}
    </template>

    <CollapseFieldset>
      <template #legend>
        General Hotspot Configuration
      </template>

      <div class="flex flex-row mb-1 items-center">
        <label class="text-xs mr-2">Name</label>
        <input ref="hotspotNameRef" class="border px-1 py-0.5 text-xs flex-grow" type="text" :value="`${hotspot.hotspotName}`">
      </div>

      <div class="flex flex-row mb-1 items-center">
        <label class="text-xs mr-2">Label Text</label>
        <input v-model="hotspot.labelText" class="border px-1 py-0.5 text-xs flex-grow" type="text">
      </div>

      <div class="flex flex-col justify-between mb-1">
        <ToggleBox v-model="hotspot.startVisible" label="Start Visible" class="mb-1" />
        <ToggleBox v-model="isFlatContent" label="Flat Content" />
      </div>

      <HotspotPerspectives v-if="isFlatContent === false" />
      <HotspotFlatContent v-if="isFlatContent === true" />
    </CollapseFieldset>

    <HotspotTargets />
    <HotspotAnimations />
    <HotspotUpdates v-if="isFlatContent === false" />

    <button type="button" class="text-xs p-1 bg-red-700 text-white my-1 w-full" @click="deleteHotspot">
      Delete Hotspot
    </button>
  </CollapseFieldset>
</template>
